import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import {
  authApi,
  LoginCredentials,
  SignupCredentials,
  VerifyEmailParams,
  ForgotPasswordParams,
  ResetPasswordParams,
  RespondToChallengeParams,
} from '../api/auth';
import { useAuthStore } from '../stores/authStore';
import { useToast } from '../components/ui/Toast';

export function useAuth() {
  const { tokens, user, setTokens, setUser, logout: logoutStore, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => authApi.login(credentials),
    onSuccess: data => {
      // Skip token setting if this is a challenge response
      if ('challenge' in data) return;

      setTokens({
        id_token: data.id_token,
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in,
      });
      showToast('Welcome back!', 'success');
      navigate('/dashboard');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Login failed', 'error');
    },
  });

  const signupMutation = useMutation({
    mutationFn: (credentials: SignupCredentials) => authApi.signup(credentials),
    onSuccess: () => {
      showToast('Account created! Please check your email for the verification code.', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Signup failed', 'error');
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (params: VerifyEmailParams) => authApi.verify(params),
    onSuccess: () => {
      showToast('Email verified! You can now log in.', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Verification failed', 'error');
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: (params: ForgotPasswordParams) => authApi.forgotPassword(params),
    onSuccess: () => {
      showToast('If an account exists, a password reset code has been sent.', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to send reset code', 'error');
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (params: ResetPasswordParams) => authApi.resetPassword(params),
    onSuccess: () => {
      showToast('Password reset successfully! You can now log in.', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Password reset failed', 'error');
    },
  });

  const challengeMutation = useMutation({
    mutationFn: (params: RespondToChallengeParams) => authApi.respondToChallenge(params),
    onSuccess: data => {
      setTokens({
        id_token: data.id_token,
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in,
      });
      showToast('Password set successfully! Welcome!', 'success');
      navigate('/dashboard');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to set new password', 'error');
    },
  });

  const logout = () => {
    logoutStore();
    queryClient.clear();
    navigate('/login');
  };

  return {
    tokens,
    user,
    isAuthenticated: isAuthenticated(),
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    signup: signupMutation.mutate,
    signupAsync: signupMutation.mutateAsync,
    isSigningUp: signupMutation.isPending,
    signupError: signupMutation.error,
    verify: verifyMutation.mutate,
    verifyAsync: verifyMutation.mutateAsync,
    isVerifying: verifyMutation.isPending,
    verifyError: verifyMutation.error,
    forgotPassword: forgotPasswordMutation.mutate,
    forgotPasswordAsync: forgotPasswordMutation.mutateAsync,
    isSendingResetCode: forgotPasswordMutation.isPending,
    resetPassword: resetPasswordMutation.mutate,
    resetPasswordAsync: resetPasswordMutation.mutateAsync,
    isResettingPassword: resetPasswordMutation.isPending,
    respondToChallenge: challengeMutation.mutate,
    respondToChallengeAsync: challengeMutation.mutateAsync,
    isRespondingToChallenge: challengeMutation.isPending,
    logout,
    setUser,
  };
}

export function useCurrentUser() {
  const { isAuthenticated } = useAuthStore();
  const { setUser } = useAuthStore();

  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const user = await authApi.me();
      setUser(user);
      return user;
    },
    enabled: isAuthenticated(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}
